import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, Typography, Paper, Button, Grid, Card, CardContent, 
  CardMedia, CardActions, TextField, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Pagination, Box, IconButton, Snackbar, Alert,
  CircularProgress, Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import { 
  getPatterns, getPatternCategories, createPattern, 
  updatePattern, deletePattern, refreshPatternVectors
} from '../services/patternService';

// 样式定义
const Input = styled('input')({
  display: 'none',
});

const PatternManagement = () => {
  // 状态管理
  const [patterns, setPatterns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 8, total: 0, totalPages: 0 });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [viewingPattern, setViewingPattern] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingVectors, setRefreshingVectors] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
    imageData: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  // 获取样板数据
  const fetchPatterns = async () => {
    try {
      setLoading(true);
      const response = await getPatterns(
        selectedCategory || null, 
        pagination.page, 
        pagination.pageSize
      );
      
      if (response.success) {
        setPatterns(response.data.patterns);
        setPagination(response.data.pagination);
      } else {
        showSnackbar('获取样板列表失败', 'error');
      }
    } catch (error) {
      console.error('获取样板列表错误', error);
      showSnackbar('获取样板列表失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 获取分类数据
  const fetchCategories = async () => {
    try {
      const response = await getPatternCategories();
      if (response.success) {
        setCategories(response.data);
      } else {
        showSnackbar('获取分类列表失败', 'error');
      }
    } catch (error) {
      console.error('获取分类列表错误', error);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchCategories();
    fetchPatterns();
  }, []);

  // 当页码或分类筛选变更时重新加载数据
  useEffect(() => {
    fetchPatterns();
  }, [pagination.page, selectedCategory]);

  // 显示提示信息
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // 关闭提示
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 处理分类筛选变更
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPagination({ ...pagination, page: 1 }); // 重置到第一页
  };

  // 处理页码变更
  const handlePageChange = (event, value) => {
    setPagination({ ...pagination, page: value });
  };

  // 处理文件选择
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imageData: reader.result });
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 处理表单输入变更
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 打开创建/编辑对话框
  const handleOpenDialog = (pattern = null) => {
    if (pattern) {
      setEditingPattern(pattern);
      setFormData({
        name: pattern.name,
        categoryId: pattern.category_id || '',
        description: pattern.description || '',
        imageData: null
      });
      setPreviewImage(pattern.image_url);
    } else {
      setEditingPattern(null);
      setFormData({
        name: '',
        categoryId: '',
        description: '',
        imageData: null
      });
      setPreviewImage(null);
    }
    setOpenDialog(true);
  };

  // 关闭对话框
  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 打开删除确认对话框
  const handleOpenDeleteDialog = (pattern) => {
    setEditingPattern(pattern);
    setOpenDeleteDialog(true);
  };

  // 关闭删除确认对话框
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  // 打开查看对话框
  const handleOpenViewDialog = (pattern) => {
    setViewingPattern(pattern);
    setOpenViewDialog(true);
  };

  // 关闭查看对话框
  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
  };

  // 刷新向量特征
  const handleRefreshVectors = async () => {
    try {
      setRefreshingVectors(true);
      const response = await refreshPatternVectors();
      
      if (response.success) {
        showSnackbar(response.message || '向量刷新成功');
      } else {
        showSnackbar(response.message || '向量刷新失败', 'error');
      }
    } catch (error) {
      console.error('刷新向量失败', error);
      showSnackbar('刷新向量失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      setRefreshingVectors(false);
    }
  };

  // 保存样板
  const handleSavePattern = async () => {
    try {
      if (!formData.name.trim()) {
        showSnackbar('样板名称不能为空', 'error');
        return;
      }

      if (!formData.categoryId) {
        showSnackbar('请选择样板分类', 'error');
        return;
      }
      
      // 图片不是必选项

      setLoading(true);
      let response;
      
      if (editingPattern) {
        // 更新
        response = await updatePattern({
          id: editingPattern.id,
          ...formData
        });
      } else {
        // 创建
        response = await createPattern(formData);
      }

      if (response.success) {
        showSnackbar(editingPattern ? '样板更新成功' : '样板添加成功');
        fetchPatterns();
        handleCloseDialog();
      } else {
        showSnackbar(response.message || '操作失败', 'error');
      }
    } catch (error) {
      console.error('保存样板错误', error);
      showSnackbar('操作失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // 删除样板
  const handleDeletePattern = async () => {
    try {
      if (!editingPattern) {
        return;
      }

      setLoading(true);
      const response = await deletePattern(editingPattern.id);
      
      if (response.success) {
        showSnackbar('样板删除成功');
        fetchPatterns();
      } else {
        showSnackbar(response.message || '删除失败', 'error');
      }
    } catch (error) {
      console.error('删除样板错误', error);
      showSnackbar('删除失败: ' + (error.message || '未知错误'), 'error');
    } finally {
      handleCloseDeleteDialog();
      setLoading(false);
    }
  };

  // 渲染样板卡片
  const renderPatternCard = (pattern) => (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardMedia
        component="div"
        sx={{
          pt: '75%',
          position: 'relative',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundColor: '#f5f5f5'
        }}
        image={pattern.image_url || '/placeholder-image.jpg'}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            p: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderBottomLeftRadius: '8px'
          }}
        >
          <Chip 
            label={pattern.category_name || '未分类'} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </CardMedia>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="h2" noWrap>
          {pattern.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          编码: {pattern.code}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {pattern.description?.length > 50 
            ? `${pattern.description.substring(0, 50)}...` 
            : pattern.description || '无描述'}
        </Typography>
      </CardContent>
      <CardActions>
        <IconButton size="small" color="primary" onClick={() => handleOpenViewDialog(pattern)}>
          <VisibilityIcon />
        </IconButton>
        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(pattern)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(pattern)}>
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          样板管理
        </Typography>
        
        {/* 过滤和操作区 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="category-select-label">分类筛选</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={selectedCategory}
              label="分类筛选"
              onChange={handleCategoryChange}
            >
              <MenuItem value="">全部分类</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleRefreshVectors}
              disabled={refreshingVectors}
            >
              {refreshingVectors ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
              刷新向量
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              添加样板
            </Button>
          </Box>
        </Box>
        
        {/* 样板卡片列表 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : patterns.length === 0 ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="body1" color="text.secondary">
              暂无样板数据
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {patterns.map((pattern) => (
              <Grid item key={pattern.id} xs={12} sm={6} md={3}>
                {renderPatternCard(pattern)}
              </Grid>
            ))}
          </Grid>
        )}
        
        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={pagination.totalPages} 
              page={pagination.page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}
      </Paper>

      {/* 创建/编辑对话框 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingPattern ? '编辑样板' : '添加样板'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                name="name"
                label="样板名称"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                required
              />
              
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <InputLabel id="category-label">样板分类</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  name="categoryId"
                  value={formData.categoryId}
                  label="样板分类"
                  onChange={handleInputChange}
                  required
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                name="description"
                label="样板描述"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                multiline
                rows={4}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: 1, 
                  p: 2, 
                  mt: 2,
                  height: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  backgroundColor: '#f9f9f9'
                }}
              >
                {previewImage ? (
                  <>
                    <img 
                      src={previewImage} 
                      alt="预览" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                    <IconButton 
                      size="small"
                      sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.7)' }}
                      onClick={() => {
                        setPreviewImage(null);
                        setFormData({ ...formData, imageData: null });
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <ImageIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                      点击上传或拖拽图片到此处
                    </Typography>
                  </>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <label htmlFor="contained-button-file">
                  <Input 
                    accept="image/*" 
                    id="contained-button-file" 
                    type="file" 
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                  <Button 
                    variant="contained" 
                    component="span"
                    startIcon={<ImageIcon />}
                  >
                    选择图片
                  </Button>
                </label>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>取消</Button>
          <Button 
            onClick={handleSavePattern} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除样板 "{editingPattern?.name}" 吗？此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>取消</Button>
          <Button 
            onClick={handleDeletePattern} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 查看样板对话框 */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>样板详情</DialogTitle>
        <DialogContent>
          {viewingPattern && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">{viewingPattern.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  编码: {viewingPattern.code}
                </Typography>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>分类:</Typography>
                <Typography variant="body1">{viewingPattern.category_name || '未分类'}</Typography>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>描述:</Typography>
                <Typography variant="body1">{viewingPattern.description || '无描述'}</Typography>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>创建时间:</Typography>
                <Typography variant="body1">
                  {new Date(viewingPattern.created_at).toLocaleString()}
                </Typography>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 2 }}>更新时间:</Typography>
                <Typography variant="body1">
                  {new Date(viewingPattern.updated_at).toLocaleString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {viewingPattern.image_url ? (
                  <Box 
                    sx={{ 
                      border: '1px solid #eee', 
                      borderRadius: 1,
                      p: 1,
                      height: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img 
                      src={viewingPattern.image_url} 
                      alt={viewingPattern.name} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      border: '1px dashed #ccc', 
                      borderRadius: 1,
                      p: 2,
                      height: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      无图片
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>关闭</Button>
        </DialogActions>
      </Dialog>

      {/* 提示消息 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PatternManagement;
